import { FunctionComponent, RefObject, useEffect, useRef, useState } from 'react'
import Webcam from 'react-webcam'
import jsQR, { QRCode } from 'jsqr'
import styles from './QRCodeScanner.module.scss'
import ButtonTextIcon from '../_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { CameraAlt } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { PermissionState } from '@/types/Permissions.ts'

interface QRCodeScannerProps {
    onScan: (data: string) => void
    isParent?: boolean
}

const QRCodeScanner: FunctionComponent<QRCodeScannerProps> = ({ onScan, isParent }: QRCodeScannerProps) => {
    const { t } = useTranslation()

    const webcamRef: RefObject<Webcam | null> = useRef<Webcam>(null)
    const [cameraPermission, setCameraPermission] = useState<boolean | null>(null)

    useEffect(() => {
        let permissionStatus: PermissionStatus | null = null

        try {
            navigator.permissions.query({ name: 'camera' }).then((result: PermissionStatus) => {
                permissionStatus = result

                setCameraPermission(result.state === PermissionState.granted)

                result.onchange = () => {
                    setCameraPermission(result.state === PermissionState.granted)
                }
            })
        } catch (error) {
            Bugsnag.notify(error as NotifiableError)
        }

        return () => {
            if (permissionStatus) permissionStatus.onchange = null
        }
    }, [])

    async function requestCameraAccess(): Promise<void> {
        try {
            await navigator.mediaDevices.getUserMedia({ video: true })
            setCameraPermission(true)
        } catch (error) {
            setCameraPermission(false)
        }
    }

    function captureAndScan(): void {
        if (!webcamRef.current) return

        const imageSrc: string | null = webcamRef.current.getScreenshot()

        if (imageSrc) {
            const image: HTMLImageElement = new Image()
            image.src = imageSrc

            image.onload = () => {
                const canvas: HTMLCanvasElement = document.createElement('canvas')
                canvas.width = image.width
                canvas.height = image.height

                const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d')
                if (ctx) {
                    ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
                    const imageData: ImageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
                    const qrCode: QRCode | null = jsQR(imageData.data, imageData.width, imageData.height)

                    if (qrCode) {
                        onScan(qrCode.data)
                    }
                }
            }
        }
    }

    useEffect(() => {
        const interval: NodeJS.Timeout = setInterval(captureAndScan, 500)
        return () => clearInterval(interval)
    }, [])

    return cameraPermission ? (
        <div className={styles.Container}>
            <div className={styles.WebcamAndCapture}>
                <Webcam
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat='image/png'
                    className={styles.Webcam}
                    videoConstraints={{
                        facingMode: 'environment'
                    }}
                />
                <div className={`${isParent ? styles.CaptureParent : styles.CaptureDaycare}`}>
                    <div></div>
                </div>
            </div>
        </div>
    ) : (
        <div className={styles.Container}>
            <ButtonTextIcon
                type={'custom'}
                parent={isParent}
                className={styles.Button}
                customText={t('authorizeCamera')}
                customIcon={<CameraAlt />}
                onClick={requestCameraAccess}
            />
        </div>
    )
}

export default QRCodeScanner
